import React, { createContext, useState, useContext, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebaseConfig';

// Create the context
const UserContext = createContext();

// Provider component
export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [uid, setUid] = useState(null);
    const [merchantData, setMerchantData] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isMerchant, setIsMerchant] = useState(false);
    const [isUser, setIsUser] = useState(false);
    // Role da equipe Auuii ('admin' | 'support' | null). Vem do CUSTOM CLAIM do
    // ID token — não de um documento. Essa distinção é a segurança inteira:
    // a claim é assinada pelo Firebase, só o Admin SDK (backend) grava, e é
    // exatamente o que o firestore.rules lê em isStaff(). Um campo tipo
    // `users/{uid}.isAdmin` seria reescrito pelo próprio usuário.
    const [role, setRole] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    // true enquanto carrega os dados (merchant/user) após resolver o uid
    const [dataLoading, setDataLoading] = useState(false);
    // uid cujo merchant/user JÁ foi resolvido. Enquanto !== user.uid, o status
    // de merchant ainda é desconhecido — evita o ProtectedRoute redirecionar
    // pro login logo após o usuário autenticar (a busca roda depois do render).
    const [resolvedUid, setResolvedUid] = useState(null);

    // Escutar mudanças de autenticação do Firebase
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);
                setUid(firebaseUser.uid);
                
                if (!localStorage.getItem('selectedStoreUid')) {
                    localStorage.setItem('selectedStoreUid', firebaseUser.uid);
                }
            } else {
                setUser(null);
                setUid(null);
                setMerchantData(null);
                setUserData(null);
                setIsMerchant(false);
                setIsUser(false);
                setRole(null);
                setResolvedUid(null);
                // Limpa cache de sessão ao deslogar (evita vazar dados entre contas)
                try {
                    Object.keys(sessionStorage)
                        .filter((k) => k.startsWith('userCtx_'))
                        .forEach((k) => sessionStorage.removeItem(k));
                } catch (e) { /* ignora */ }
            }
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Buscar dados do merchant e do user EM PARALELO quando uid muda.
    // Usa cache em sessionStorage para mostrar os dados INSTANTANEAMENTE ao trocar
    // de tela / recarregar, revalidando do Firestore em background (stale-while-revalidate).
    useEffect(() => {
        if (!uid) return;

        let cancelled = false;
        const cacheKey = `userCtx_${uid}`;

        // 1) Carrega do cache imediatamente (sem esperar a rede)
        let hadCache = false;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed.merchantData) { setMerchantData(parsed.merchantData); setIsMerchant(true); }
                if (parsed.userData) { setUserData(parsed.userData); setIsUser(true); }
                if (parsed.role) setRole(parsed.role);
                hadCache = true;
                setDataLoading(false); // já temos algo pra mostrar
                setResolvedUid(uid);   // status conhecido pelo cache → libera o ProtectedRoute
            }
        } catch (e) { /* cache inválido, ignora */ }

        // 2) Revalida do Firestore (sempre, mas em background se já havia cache)
        const fetchData = async (isRetry = false) => {
            // A role sai do ID token, não do Firestore — então é resolvida ANTES
            // e com try/catch próprio. Se estivesse no mesmo Promise.all das
            // leituras abaixo, um permission-denied em qualquer doc derrubaria
            // junto a claim e trancaria o admin fora do painel de admin.
            let claimRole = null;
            try {
                if (auth.currentUser) {
                    const tokenResult = await auth.currentUser.getIdTokenResult();
                    claimRole = tokenResult?.claims?.role || null;
                }
            } catch (err) {
                console.warn('[UserContext] não foi possível ler as claims do token:', err?.code || err?.message);
            }
            if (cancelled) return;
            setRole(claimRole);

            try {
                const [merchantDocSnap, userDocSnap] = await Promise.all([
                    getDoc(doc(db, 'merchants', uid)),
                    getDoc(doc(db, 'users', uid)),
                ]);

                if (cancelled) return;

                const fresh = { merchantData: null, userData: null, role: claimRole };

                if (merchantDocSnap.exists()) {
                    fresh.merchantData = merchantDocSnap.data();
                    setMerchantData(fresh.merchantData);
                    setIsMerchant(true);
                } else {
                    setMerchantData(null);
                    setIsMerchant(false);
                }

                if (userDocSnap.exists()) {
                    fresh.userData = userDocSnap.data();
                    setUserData(fresh.userData);
                    setIsUser(true);
                } else {
                    setUserData(null);
                    setIsUser(false);
                }

                // Atualiza o cache
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(fresh));
                } catch (e) { /* quota cheia, ignora */ }
            } catch (error) {
                // Token "dormente"/desatualizado no boot pode causar permission-denied
                // mesmo com sessão válida. Força um refresh do ID token e tenta 1x.
                if (!isRetry && error?.code === 'permission-denied' && auth.currentUser) {
                    try {
                        await auth.currentUser.getIdToken(true);
                        if (!cancelled) return fetchData(true);
                    } catch (_) { /* token realmente inválido → cai no fluxo de erro abaixo */ }
                }
                console.warn('[UserContext] dados do usuário/merchant indisponíveis:', error?.code || error?.message);
                if (!cancelled && !hadCache) {
                    setIsMerchant(false);
                    setIsUser(false);
                    // A role NÃO é zerada aqui: ela já foi resolvida do token
                    // acima, e este catch é só sobre as leituras do Firestore.
                }
            } finally {
                if (!cancelled) {
                    setDataLoading(false);
                    setResolvedUid(uid); // merchant/user resolvidos para este uid
                }
            }
        };

        if (!hadCache) setDataLoading(true);
        fetchData();

        return () => { cancelled = true; };
    }, [uid]);

    return (
        <UserContext.Provider value={{
            user,
            setUser,
            uid,
            setUid,
            merchantData,
            setMerchantData,
            userData,
            setUserData,
            isMerchant,
            isUser,
            role,
            // Só 'admin' abre o acesso a todas as lojas. 'support' (atendimento)
            // usa o painel de suporte próprio; aqui não entra.
            isAdmin: role === 'admin',
            authLoading,
            dataLoading,
            resolvedUid
        }}>
            {children}
        </UserContext.Provider>
    );
};

// Custom hook to use the UserContext
export const useUser = () => {
    return useContext(UserContext);
};
